import moment from "moment";

// Calendar item type constants
export const CALENDAR_ITEM_TYPES = {
  EVENT: 1,
  TASK: 2,
  REMINDER: 3,
};

/**
 * Transform event form state to API payload format
 * @param {Object} formState - Event form state from CalendarEvents component
 * @param {Array} selectedUserIds - Array of selected user IDs
 * @param {string} title - Event title
 * @param {string} location - Event location (optional)
 * @param {string} description - Event description (optional)
 * @returns {Object} API payload
 */
export const transformEventFormToApi = (
  formState,
  selectedUserIds = [],
  title = "",
  location = "",
  description = ""
) => {
  const {
    startDate,
    endDate,
    eventStartTime,
    eventEndTime,
    isEventAllDay,
    selectedColor,
    eventRepeat,
    notifications,
    // Custom recurrence fields
    customRepeatEvery,
    customRepeatUnit,
    customMonthlyPattern,
    customWeeklyDays,
    customEndMode,
    customEndOnDate,
    customEndAfterCount,
  } = formState;

  // Format dates
  const start_date = startDate ? moment(startDate).format("YYYY-MM-DD") : null;
  // If end_date is not set, use start_date (required by API)
  const end_date = endDate ? moment(endDate).format("YYYY-MM-DD") : start_date;

  // Build payload
  const payload = {
    type: CALENDAR_ITEM_TYPES.EVENT,
    title: title || "Untitled Event",
    color: selectedColor || "#1e88e5",
    user_ids: selectedUserIds,
    start_date,
    end_date,
    only_day: isEventAllDay || false,
  };

  // Add time fields if not all-day
  if (!isEventAllDay && eventStartTime && eventEndTime) {
    // API expects H:i (hours:minutes, no seconds)
    payload.start_time = moment(eventStartTime).format("HH:mm");
    payload.end_time = moment(eventEndTime).format("HH:mm");
  }

  // Add optional fields
  if (location) {
    payload.location = location;
  }

  if (description) {
    payload.description = description;
  }

  // Add recurrence if set
  if (eventRepeat && eventRepeat !== "none") {
    payload.repeat = eventRepeat;

    // Add custom recurrence details when eventRepeat is "custom"
    if (eventRepeat === "custom") {
      payload.repeat_times = customRepeatEvery || 1;
      payload.repeat_duration_type = customRepeatUnit || "day";

      // Add weekly days if repeat unit is week
      if (customRepeatUnit === "week" && customWeeklyDays?.length > 0) {
        payload.repeat_weekly_days = customWeeklyDays;
      }

      // Add monthly pattern if repeat unit is month
      // month_on: 1 = day of month (e.g., "Monthly on day 15")
      // month_on: 2 = weekday position (e.g., "Monthly on the third Monday")
      if (customRepeatUnit === "month") {
        payload.month_on = customMonthlyPattern === "day-of-month" ? 1 : 2;
      }

      // Add end conditions
      // end_type: "date" → ends_on = formatted date (e.g., "2026-01-31")
      // end_type: "count" → ends_on = number of occurrences (e.g., 5)
      if (customEndMode === "on" && customEndOnDate) {
        payload.end_type = "date";
        payload.ends_on = moment(customEndOnDate).format("YYYY-MM-DD");
      } else if (customEndMode === "after" && customEndAfterCount) {
        payload.end_type = "count";
        payload.ends_on = customEndAfterCount;
      }
    }
  }

  // Add notifications if any
  if (notifications && notifications.length > 0) {
    payload.notifications = notifications.map((notif) => ({
      amount: notif.amount,
      unit: notif.unit.toLowerCase(),
      // Keep notification time consistent with event time format (H:i)
      time: notif.time ? moment(notif.time).format("HH:mm") : null,
    }));
  }

  return payload;
};

/**
 * Transform task form state to API payload format
 * @param {Object} formState - Task form state
 * @param {Array} selectedUserIds - Array of selected user IDs
 * @param {string} title - Task title
 * @param {string} description - Task description (optional)
 * @returns {Object} API payload
 */
export const transformTaskFormToApi = (
  formState,
  selectedUserIds = [],
  title = "",
  description = ""
) => {
  const { dueDate, startTime, endTime, isAllDay, selectedColor } = formState;

  // Format due date - API expects start_date and end_date (same for tasks)
  const formattedDate = dueDate ? moment(dueDate).format("YYYY-MM-DD") : null;

  const payload = {
    type: CALENDAR_ITEM_TYPES.TASK,
    title: title || "Untitled Task",
    color: selectedColor || "#33FF57",
    user_ids: selectedUserIds,
    start_date: formattedDate,
    end_date: formattedDate,
    only_day: isAllDay ? 1 : 0,
  };

  // Add time fields if not all-day task
  if (!isAllDay && startTime && endTime) {
    payload.start_time = moment(startTime).format("HH:mm");
    payload.end_time = moment(endTime).format("HH:mm");
  }

  if (description) {
    payload.description = description;
  }

  return payload;
};

/**
 * Transform reminder form state to API payload format
 * @param {Object} formState - Reminder form state
 * @param {Array} selectedUserIds - Array of selected user IDs
 * @param {string} title - Reminder title
 * @param {string} description - Reminder description (optional)
 * @returns {Object} API payload
 */
export const transformReminderFormToApi = (
  formState,
  selectedUserIds = [],
  title = "",
  description = ""
) => {
  const { reminderDate, startTime, endTime, isAllDay, selectedColor } =
    formState;

  // Format date - API expects start_date and end_date (same for reminders)
  const formattedDate = reminderDate
    ? moment(reminderDate).format("YYYY-MM-DD")
    : null;

  const payload = {
    type: CALENDAR_ITEM_TYPES.REMINDER,
    title: title || "Untitled Reminder",
    color: selectedColor || "#FFC300",
    user_ids: selectedUserIds,
    start_date: formattedDate,
    end_date: formattedDate,
    only_day: isAllDay ? 1 : 0,
  };

  // Add time fields if not all-day reminder
  if (!isAllDay && startTime && endTime) {
    payload.start_time = moment(startTime).format("HH:mm");
    payload.end_time = moment(endTime).format("HH:mm");
  }

  if (description) {
    payload.description = description;
  }

  return payload;
};

/**
 * Transform API response to FullCalendar event format
 * @param {Object} apiData - Event data from API
 * @returns {Object} FullCalendar event object
 */
export const transformApiToCalendarEvent = (apiData) => {
  const {
    id, // internal calendar item id
    title,
    color,
    start_date,
    end_date,
    start_time,
    end_time,
    only_day,
    type,
  } = apiData;

  // Build start datetime
  const start = moment(start_date);
  if (!only_day && start_time) {
    const [hours, minutes, seconds] = start_time.split(":");
    start.hour(parseInt(hours, 10));
    start.minute(parseInt(minutes, 10));
    start.second(parseInt(seconds || 0, 10));
  }

  // Build end datetime
  const end = end_date ? moment(end_date) : start.clone().add(1, "hour");
  if (!only_day && end_time) {
    const [hours, minutes, seconds] = end_time.split(":");
    end.hour(parseInt(hours, 10));
    end.minute(parseInt(minutes, 10));
    end.second(parseInt(seconds || 0, 10));
  } else if (only_day) {
    end.endOf("day");
  }

  return {
    id: id?.toString(),
    title: title || "Untitled",
    start: start.toDate(),
    end: end.toDate(),
    allDay: only_day || false,
    backgroundColor: color || "#1e88e5",
    borderColor: color || "#1e88e5",
    extendedProps: {
      type,
      ...apiData,
    },
  };
};
